import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PayPalScriptProvider, PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { api, coverUrl } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Download, Loader2, ShieldCheck, KeyRound, Package } from "lucide-react";
import { useAccessCode } from "@/context/AccessCodeContext";
import { useAuth } from "@/context/AuthContext";
import { LoginRequiredBody } from "@/components/LoginPrompt";
import { downloadTrackUrl, downloadPackUrl, downloadVersionUrl } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const downloadOrderUrl = (id) => `${API}/download/bizum/${id}`;

let ppConfigCache = null;

export default function BizumModal({ open, onClose, item: itemProp, itemType: itemTypeProp }) {
  const [asPack, setAsPack] = useState(false);
  const upsell = itemTypeProp === "pista" && itemProp?.pack_id && Number(itemProp.pack_price) > 0 && Number(itemProp.price) > 0 && itemProp.pack_tracks > 1;
  const savings = upsell ? Number(itemProp.pack_tracks) * Number(itemProp.price) - Number(itemProp.pack_price) : 0;
  const item = asPack && upsell ? { id: itemProp.pack_id, title: itemProp.pack_title || "Pack completo", price: itemProp.pack_price, cover_path: itemProp.cover_path, track_count: itemProp.pack_tracks } : itemProp;
  const itemType = asPack && upsell ? "pack" : itemTypeProp;
  const [step, setStep] = useState("pay"); // pay | capturing | paid | error
  const [order, setOrder] = useState(null);
  const [pp, setPp] = useState(ppConfigCache);
  const [error, setError] = useState("");
  const ac = useAccessCode();
  const { user } = useAuth();
  const [codeInput, setCodeInput] = useState("");
  const [codeBusy, setCodeBusy] = useState(false);
  const itemId = item?.track_id || item?.id;
  const unlocked = itemType === "pista" ? ac.allows("pack", item?.pack_id) : ac.allows(itemType, itemId);
  const freeUrl = item?.download_url || (itemType === "pack" ? downloadPackUrl(itemId) : item?.version_id ? downloadVersionUrl(itemId, item.version_id) : downloadTrackUrl(itemId));
  const redeem = async (e) => {
    e.preventDefault();
    setCodeBusy(true);
    try { (await ac.redeem(codeInput)) ? toast.success("Código activado") : toast.error("Código no válido"); } catch { toast.error("Error al comprobar"); } finally { setCodeBusy(false); }
  };

  useEffect(() => {
    if (!open || pp) return;
    api.get("/paypal/config").then((r) => { ppConfigCache = r.data; setPp(r.data); }).catch(() => setPp({ enabled: false }));
  }, [open, pp]);

  const reset = () => { setStep("pay"); setOrder(null); setError(""); setAsPack(false); };
  const priceLabel = `€${Number(item?.price || 0).toFixed(2)}`;

  const createOrder = async () => {
    const res = await api.post("/paypal/orders", { item_type: itemType, item_id: itemId, version_id: item.version_id || null, buyer_email: "" });
    setOrder(res.data);
    return res.data.paypal_order_id;
  };

  const onApprove = async (data, actions) => {
    setStep("capturing");
    try {
      const res = await api.post(`/paypal/orders/${data.orderID}/capture`);
      setOrder((o) => ({ ...o, ...res.data }));
      setStep("paid");
      toast.success("Pago completado. Tu descarga ha comenzado.");
      window.location.assign(downloadOrderUrl(res.data.order_id));
    } catch (e) {
      const d = e?.response?.data?.detail;
      const issue = typeof d === "object" ? d?.issue : null;
      if (issue === "INSTRUMENT_DECLINED" && actions?.restart) {
        toast.error("Tu método de pago fue rechazado. Elige otro.");
        setStep("pay");
        return actions.restart();
      }
      setError(typeof d === "object" ? (d?.message || "No se pudo confirmar el pago") : (d || "No se pudo confirmar el pago"));
      setStep("error");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="bg-[#0f0f14] border border-white/10 text-white w-[calc(100vw-2rem)] max-w-md overflow-hidden" data-testid="paypal-modal">
        <DialogHeader>
          <DialogTitle className="font-display font-black uppercase text-xl tracking-tight">
            {step === "paid" ? "¡Pago completado!" : "Comprar"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-3 items-center p-3 rounded-lg border border-white/10 bg-white/5 w-full min-w-0" data-testid="paypal-item-summary">
          <div className="w-14 h-14 rounded-md overflow-hidden bg-neutral-900 shrink-0">
            {item?.cover_path && <img src={coverUrl(item.cover_path, 160)} alt="" className="w-full h-full object-cover" />}
          </div>
          <div className="flex-1 min-w-0 overflow-hidden">
            <div className="font-bold leading-snug line-clamp-2 break-words">{item?.title}</div>
            {item?.version_title && <div className="text-[11px] text-red-400/90 font-mono uppercase tracking-wider truncate mt-0.5">{item.version_title}</div>}
            <div className="text-xs text-white/50 truncate">{itemType !== "pack" ? item?.artist : `Pack · ${item?.tracks?.length || item?.track_count || 0} tracks`}</div>
            {itemType === "pista" && item?.pack_title && <div className="text-[10px] text-sky-400 font-mono uppercase tracking-wider truncate mt-0.5" data-testid="paypal-pista-pack">Pista suelta del pack «{item.pack_title}»</div>}
          </div>
          <div className="text-red-500 font-display font-black text-lg shrink-0" data-testid="paypal-item-price">{priceLabel}</div>
        </div>

        {!user && <LoginRequiredBody reason="buy" testid="paypal-login-required" />}

        {user && step === "pay" && unlocked && (
          <div className="text-center space-y-3 mt-1" data-testid="code-unlocked">
            <div className="text-sm text-emerald-400 flex items-center justify-center gap-2"><KeyRound className="w-4 h-4" /> Incluido en tu código de acceso</div>
            <a href={ac.withCode(freeUrl)} data-testid="code-download-button" className="inline-flex items-center gap-2 btn-red text-white px-6 py-3 rounded-full font-semibold">
              <Download className="w-4 h-4" /> Descargar gratis
            </a>
          </div>
        )}

        {user && step === "pay" && !unlocked && (
          <div className="space-y-3 mt-1 w-full min-w-0" data-testid="paypal-buttons-wrap">
            {upsell && !asPack && savings > 0 && (
              <button type="button" onClick={() => setAsPack(true)} data-testid="paypal-upsell-pack"
                className="w-full text-left rounded-xl border border-sky-800/60 bg-sky-950/30 hover:bg-sky-900/40 transition-colors p-3 flex items-center gap-3">
                <Package className="w-5 h-5 text-sky-400 shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold text-white">Ahórrate €{savings.toFixed(2)} llevándote el pack completo</span>
                  <span className="block text-xs text-white/60 truncate">{itemProp.pack_tracks} pistas por €{Number(itemProp.pack_price).toFixed(2)} · «{itemProp.pack_title}»</span>
                </span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-sky-400 shrink-0">Ver →</span>
              </button>
            )}
            {asPack && (
              <button type="button" onClick={() => setAsPack(false)} data-testid="paypal-upsell-back" className="text-xs text-white/50 hover:text-white underline underline-offset-2">← Volver a la pista suelta (€{Number(itemProp.price).toFixed(2)})</button>
            )}
            {!pp ? (
              <div className="flex items-center justify-center gap-2 text-white/50 text-sm py-4"><Loader2 className="w-4 h-4 animate-spin" /> Cargando PayPal…</div>
            ) : !pp.enabled ? (
              <div className="text-sm text-red-400 py-3" data-testid="paypal-disabled">Pagos no disponibles en este momento.</div>
            ) : (
              <PayPalScriptProvider options={{ clientId: pp.client_id, currency: pp.currency || "EUR", intent: "capture", components: "buttons" }}>
                <ScriptState />
                <SafeButtons createOrder={createOrder} onApprove={onApprove}
                  onError={(e) => { console.error(e); setError("PayPal no pudo iniciar el pago"); setStep("error"); }}
                  onCancel={() => toast("Pago cancelado")} />
              </PayPalScriptProvider>
            )}
            <div className="flex items-center gap-2 text-[11px] text-white/40 justify-center">
              <ShieldCheck className="w-3.5 h-3.5" /> Pago seguro con PayPal · el precio se verifica en el servidor · descarga inmediata
            </div>
            <form onSubmit={redeem} className="flex items-center gap-2 pt-1" data-testid="code-form">
              <input value={codeInput} onChange={(e) => setCodeInput(e.target.value.toUpperCase())} placeholder="¿Tienes un código de acceso?" data-testid="code-input"
                className="flex-1 bg-[#16161D] border border-white/10 rounded-lg px-3 py-2 text-xs text-white font-mono uppercase outline-none focus:border-red-600/60" />
              <button type="submit" disabled={codeBusy || !codeInput.trim()} data-testid="code-submit" className="text-xs px-3 py-2 rounded-lg border border-white/15 text-white/70 hover:text-white hover:border-red-600/50 disabled:opacity-40">Usar</button>
            </form>
          </div>
        )}

        {step === "capturing" && (
          <div className="flex items-center justify-center gap-2 text-white/60 text-sm py-6" data-testid="paypal-capturing">
            <Loader2 className="w-4 h-4 animate-spin" /> Confirmando el pago con PayPal…
          </div>
        )}

        {step === "paid" && order && (
          <div className="text-center space-y-4 mt-2" data-testid="paypal-paid">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
            <p className="text-white/70">Gracias por tu compra. Si la descarga no empieza sola, pulsa el botón.</p>
            <a href={downloadOrderUrl(order.order_id)} data-testid="paypal-download-button"
              className="inline-flex items-center gap-2 btn-red text-white px-6 py-3 rounded-full font-semibold">
              <Download className="w-4 h-4" /> Descargar
            </a>
          </div>
        )}

        {step === "error" && (
          <div className="text-center py-4 space-y-3" data-testid="paypal-error">
            <p className="text-red-500 font-semibold">{error}</p>
            <button type="button" onClick={reset} className="text-xs text-white/60 hover:text-white underline">Intentar de nuevo</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SafeButtons(props) {
  const [{ isResolved }] = usePayPalScriptReducer();
  const [k, setK] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    if (!isResolved || k > 3) return;
    const t = setTimeout(() => {
      const box = ref.current?.querySelector(".paypal-buttons");
      if (!box || box.children.length === 0 || !ref.current.querySelector("iframe")) setK((x) => x + 1);
    }, 5000);
    return () => clearTimeout(t);
  }, [k, isResolved]);
  return (
    <div ref={ref} data-testid="paypal-buttons-host">
      <PayPalButtons key={k} className="w-full" style={{ layout: "vertical", color: "gold", shape: "pill", label: "pay", height: 45 }} forceReRender={[k]} {...props} />
      {k > 3 && <div className="text-xs text-amber-400 text-center" data-testid="paypal-buttons-stuck">PayPal no termina de cargar. Recarga la página e inténtalo de nuevo.</div>}
    </div>
  );
}

function ScriptState() {
  const [{ isPending, isRejected }] = usePayPalScriptReducer();
  if (isRejected) return <div className="text-sm text-red-400 py-3 text-center" data-testid="paypal-sdk-error">No se pudo cargar PayPal. Inténtalo más tarde.</div>;
  if (isPending) return <div className="flex items-center justify-center gap-2 text-white/50 text-sm py-3"><Loader2 className="w-4 h-4 animate-spin" /> Cargando PayPal…</div>;
  return null;
}
